import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  FileText, Upload, Trash2, Download, Copy, RefreshCw, 
  File, FileSpreadsheet, FileImage, FileArchive, Search,
  CheckCircle, X, FolderOpen
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

const BRAND_BLUE = '#2B4B6A';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// File type icons
const getFileIcon = (fileType) => {
  const type = fileType?.toLowerCase();
  if (['.pdf'].includes(type)) return <FileText className="w-5 h-5 text-red-500" />;
  if (['.doc', '.docx'].includes(type)) return <FileText className="w-5 h-5 text-blue-500" />;
  if (['.xls', '.xlsx', '.csv'].includes(type)) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].includes(type)) return <FileImage className="w-5 h-5 text-purple-500" />;
  if (['.zip', '.rar', '.7z'].includes(type)) return <FileArchive className="w-5 h-5 text-yellow-600" />;
  return <File className="w-5 h-5 text-gray-500" />;
};

// Format file size
const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState({ total_documents: 0, total_size: 0 });
  const [search, setSearch] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const [docsRes, statsRes] = await Promise.all([
        api.get('/admin/documents/'),
        api.get('/admin/documents/stats')
      ]);
      setDocuments(docsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        await api.post('/admin/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        errorCount++;
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || 'Unknown error'}`);
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded successfully`);
      fetchDocuments();
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) return;

    try {
      await api.delete(`/admin/documents/${docId}`);
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleCopyLink = (filePath) => {
    const fullUrl = resolveFileUrl(filePath, API_URL);
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(Array.from(e.target.files));
    }
  };

  // Filter documents by search
  const filteredDocuments = documents.filter(doc => 
    doc.original_filename?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Sidebar>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800">Admin Documents</h1>
            <p className="text-slate-500 mt-1">Upload and manage documents to share with users</p>
          </div>
          <button
            onClick={fetchDocuments}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl transition-all hover:opacity-90"
            style={{ backgroundColor: BRAND_BLUE }}
            data-testid="refresh-documents"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Documents</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_documents}</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                <FolderOpen className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Size</p>
                <p className="text-2xl font-bold text-gray-900">{formatSize(stats.total_size)}</p>
              </div>
              <div className="p-3 rounded-xl bg-green-50 text-green-600">
                <FileText className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div
          className={`relative mb-6 border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
            dragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 bg-white hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          data-testid="upload-dropzone"
        >
          <input
            type="file"
            multiple
            onChange={handleFileInputChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.png,.jpg,.jpeg,.gif,.webp,.bmp,.txt,.csv"
            data-testid="file-input"
          />
          
          <div className="flex flex-col items-center gap-3">
            <div className={`p-4 rounded-full ${dragActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
              <Upload className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-700">
                {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                PDF, Word, Excel, PowerPoint, ZIP, Images, TXT, CSV
              </p>
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-blue-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Uploading files...</span>
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="p-4 border-b border-gray-100">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                data-testid="search-documents"
              />
            </div>
          </div>

          {/* Documents Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{search ? 'No documents match your search' : 'No documents uploaded yet'}</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">File Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Size</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(doc.uploaded_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.file_type)}
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate max-w-[300px]" title={doc.original_filename}>
                              {doc.original_filename}
                            </p>
                            <p className="text-xs text-gray-500">{doc.file_type?.toUpperCase()}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatSize(doc.file_size)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {/* Download */}
                          <a
                            href={resolveFileUrl(doc.file_path, API_URL)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="Download"
                            data-testid={`download-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          
                          {/* Copy Link */}
                          <button
                            onClick={() => handleCopyLink(doc.file_path)}
                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                            title="Copy Link"
                            data-testid={`copy-link-${doc.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          
                          {/* Delete */}
                          <button
                            onClick={() => handleDelete(doc.id, doc.original_filename)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Delete"
                            data-testid={`delete-${doc.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminDocuments;
